/**
 * @geenius/tools-errors — Structured error handling
 *
 * Provides a hierarchy of typed errors for different error scenarios.
 * Enables consistent error handling and formatting across the Geenius ecosystem.
 */

/**
 * Error codes for different error types
 */
export enum ErrorCode {
  // Generic
  UNKNOWN = 'UNKNOWN',
  VALIDATION = 'VALIDATION',
  NOT_FOUND = 'NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  CONFLICT = 'CONFLICT',
  INTERNAL = 'INTERNAL',

  // Auth-specific
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  MFA_REQUIRED = 'MFA_REQUIRED',
  INVALID_TOKEN = 'INVALID_TOKEN',

  // Database
  DATABASE_ERROR = 'DATABASE_ERROR',
  CONSTRAINT_VIOLATION = 'CONSTRAINT_VIOLATION',

  // Network
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',

  // Configuration
  INVALID_CONFIG = 'INVALID_CONFIG',
  MISSING_CONFIG = 'MISSING_CONFIG',
}

/**
 * Base Geenius error with structured information
 */
export interface GeeniusErrorOptions {
  /** Error code for classification */
  code?: ErrorCode
  /** User-friendly message */
  message: string
  /** Technical details */
  cause?: Error | unknown
  /** HTTP status code equivalent */
  statusCode?: number
  /** Contextual data */
  context?: Record<string, unknown>
  /** Whether error should be logged */
  log?: boolean
}

/**
 * Structured error class for Geenius packages
 *
 * @example
 * ```ts
 * throw new GeeniusError({
 *   code: ErrorCode.VALIDATION,
 *   message: 'Invalid email format',
 *   statusCode: 400,
 *   context: { field: 'email' }
 * })
 * ```
 */
export class GeeniusError extends Error {
  readonly code: ErrorCode
  readonly statusCode: number
  readonly context: Record<string, unknown>
  readonly cause?: Error | unknown
  readonly log: boolean

  constructor(options: GeeniusErrorOptions) {
    super(options.message)
    this.name = 'GeeniusError'
    this.code = options.code ?? ErrorCode.UNKNOWN
    this.statusCode = options.statusCode ?? this.getDefaultStatusCode(this.code)
    this.context = options.context ?? {}
    this.cause = options.cause
    this.log = options.log !== false

    // Maintain proper prototype chain
    Object.setPrototypeOf(this, GeeniusError.prototype)
  }

  /**
   * Gets HTTP status code for error code
   */
  private getDefaultStatusCode(code: ErrorCode): number {
    const statusMap: Record<ErrorCode, number> = {
      [ErrorCode.UNKNOWN]: 500,
      [ErrorCode.VALIDATION]: 400,
      [ErrorCode.NOT_FOUND]: 404,
      [ErrorCode.UNAUTHORIZED]: 401,
      [ErrorCode.FORBIDDEN]: 403,
      [ErrorCode.CONFLICT]: 409,
      [ErrorCode.INTERNAL]: 500,
      [ErrorCode.INVALID_CREDENTIALS]: 401,
      [ErrorCode.SESSION_EXPIRED]: 401,
      [ErrorCode.MFA_REQUIRED]: 403,
      [ErrorCode.INVALID_TOKEN]: 401,
      [ErrorCode.DATABASE_ERROR]: 500,
      [ErrorCode.CONSTRAINT_VIOLATION]: 409,
      [ErrorCode.NETWORK_ERROR]: 502,
      [ErrorCode.TIMEOUT]: 504,
      [ErrorCode.INVALID_CONFIG]: 500,
      [ErrorCode.MISSING_CONFIG]: 500,
    }
    return statusMap[code]
  }

  /**
   * Converts error to JSON for serialization
   */
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      statusCode: this.statusCode,
      context: this.context,
      ...(this.cause ? { cause: formatError(this.cause) } : {}),
    }
  }

  /**
   * Converts error to string representation
   */
  toString(): string {
    return `${this.name} [${this.code}]: ${this.message}`
  }
}

/**
 * Validation error for form/input validation failures
 *
 * @example
 * ```ts
 * throw new ValidationError({
 *   message: 'Invalid input',
 *   context: { errors: { email: 'Invalid format' } }
 * })
 * ```
 */
export class ValidationError extends GeeniusError {
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.VALIDATION,
      statusCode: 400,
    })
    this.name = 'ValidationError'
    Object.setPrototypeOf(this, ValidationError.prototype)
  }
}

/**
 * Authentication error
 */
export class AuthError extends GeeniusError {
  constructor(options: Omit<GeeniusErrorOptions, 'statusCode'>) {
    super({
      ...options,
      code: options.code ?? ErrorCode.INVALID_CREDENTIALS,
      statusCode: 401,
    })
    this.name = 'AuthError'
    Object.setPrototypeOf(this, AuthError.prototype)
  }
}

/**
 * Not found error
 */
export class NotFoundError extends GeeniusError {
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.NOT_FOUND,
      statusCode: 404,
    })
    this.name = 'NotFoundError'
    Object.setPrototypeOf(this, NotFoundError.prototype)
  }
}

/**
 * Conflict error (e.g., duplicate key)
 */
export class ConflictError extends GeeniusError {
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.CONFLICT,
      statusCode: 409,
    })
    this.name = 'ConflictError'
    Object.setPrototypeOf(this, ConflictError.prototype)
  }
}

/**
 * Formats any error into structured format
 *
 * @param error Error to format
 * @returns Formatted error object
 */
export function formatError(error: unknown): Record<string, unknown> {
  if (error instanceof GeeniusError) {
    return error.toJSON()
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    }
  }

  if (typeof error === 'string') {
    return { message: error }
  }

  if (typeof error === 'object' && error !== null) {
    return error as Record<string, unknown>
  }

  return { message: String(error) }
}

/**
 * Type guard to check if error is GeeniusError
 *
 * @param error Error to check
 * @returns True if error is GeeniusError
 */
export function isGeeniusError(error: unknown): error is GeeniusError {
  return error instanceof GeeniusError
}

/**
 * Type guard to check if error is a specific error code
 *
 * @param error Error to check
 * @param code Error code to match
 * @returns True if error has specified code
 */
export function isErrorCode(error: unknown, code: ErrorCode): boolean {
  return isGeeniusError(error) && error.code === code
}

/**
 * Wraps a promise to catch and transform errors
 *
 * @param promise Promise to wrap
 * @param handler Error transformation function
 * @returns Promise with transformed errors
 *
 * @example
 * ```ts
 * const result = await catchError(
 *   fetchApi(),
 *   (err) => new GeeniusError({
 *     code: ErrorCode.NETWORK_ERROR,
 *     message: 'API request failed',
 *     cause: err
 *   })
 * )
 * ```
 */
export async function catchError<T>(
  promise: Promise<T>,
  handler: (error: unknown) => Error
): Promise<T> {
  try {
    return await promise
  } catch (error) {
    throw handler(error)
  }
}

/**
 * Creates a safe function that handles errors
 *
 * @param fn Function to wrap
 * @param handler Error transformation
 * @returns Safe version of function
 *
 * @example
 * ```ts
 * const safeOp = makeSafe(
 *   (id: string) => db.user.find(id),
 *   (err) => new NotFoundError({ message: 'User not found', cause: err })
 * )
 * ```
 */
export function makeSafe<T extends (...args: any[]) => any>(
  fn: T,
  handler: (error: unknown) => Error
): T {
  return ((...args: any[]) => {
    try {
      const result = fn(...args)
      if (result instanceof Promise) {
        return result.catch((err: unknown) => {
          throw handler(err)
        })
      }
      return result
    } catch (error) {
      throw handler(error)
    }
  }) as T
}
